import axios from "axios";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import React, { useEffect, useRef, useState } from "react";
import { buildResolveUrl, fetchJson } from "../../utils/util_functions";
import Column from "../nav/Column";
import Dropdown from "../nav/Dropdown";
import Link from "../nav/Link";
import Menu from "../nav/Menu";

export default function Profile({ profile: profileProp, isLoaded: isLoadedProp, isDarkTheme, ...props }) {
  const elementRef = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [isUser, setUser] = useState(false);
  const [isTrial, setTrial] = useState(false);
  const [isInternal, setInternal] = useState(false);
  // Use profile from props if available, otherwise use internal state
  const [profileData, setProfileData] = useState(profileProp || null);
  const [isLoaded, setLoaded] = useState(isLoadedProp || false);
  const ldClient = useLDClient();
  const [menuData, setMenuData] = useState([]);
  const [profileIcon, setProfileIcon] = useState([<span key={`icon`} className={`aps__profile-icon`}></span>]);
  const [isPartOfPilot, setIsPartOfPilot] = useState(false);
  const flags = useFlags();

  useEffect(() => {
    // If profile is passed as prop, use it and mark as loaded
    if (isLoadedProp) {
      setProfileData(profileProp || null);
      setLoaded(isLoadedProp);
    }
  }, [profileProp, isLoadedProp]);

  useEffect(() => {
    if(profileData){
    // Check if user is logged in
    setUser(profileData.email?.length && profileData.message === undefined);
    // Check if user is in trial state
    setTrial(profileData.subscription_status === 'forge-team-free');
    setInternal(profileData.subscription_status === 'forge-internal');
    // Set profile icon
    if (profileData.oxygen_avatar_url) {
      setProfileIcon([
        <span key={`icon`} className={`aps__profile-icon`}>
            <img src={profileData.oxygen_avatar_url}/>
        </span>
      ]);
    }
    if (ldClient && profileData.analytics_id) {
      ldClient.identify({
        kind: "user",
        key: profileData.analytics_id,
        groups: profileData?.groups,
        is_internal: profileData?.is_internal,
      });
    }
  }
  }, [profileData, ldClient]);

  useEffect(() => {
    // Load menu data based on profile (only if user is logged in)
    if (profileProp && profileProp.email) {
      setProfileData(profileProp || null);
      // Load menu data based on profile
      let profileType = 'profile-menu';
      let mobileModifier = "";
      if (profileProp.licenses?.includes('APSDVHUB')) {
        if (document.documentElement.clientWidth <= 1024)
          mobileModifier = '-mobile'
        profileType = 'profile-hub-admin';
      }
      let path = process.env.CMS_API + profileType + mobileModifier + (process.env.NODE_ENV == 'local' ? '.json' : '?_format=json');
      fetchJson(path).then((json) => {
        setMenuData(json);
      });
      
      // Check if user is part of the pilot
      let cancel = false;
      function checkIfIsPartOfThePilot() {
        axios.get("/applications/v1/contexts?include=subscriptionPilotPlan")
          .then(({ data }) => {
            if (cancel === true) return;
            const teams = data.results;
            const isPartOfPilot = teams.some((team) => ["tier1", "tier2"].includes(team.subscriptionPilotPlan?.cohort));
            if (isPartOfPilot) setIsPartOfPilot(true);
          })
          .catch((e) => {
            console.error(e);
            return false;
          });
      }
      checkIfIsPartOfThePilot();
  
      return function cleanup() {
        cancel = true;
      };
    } 
  }, [profileProp]);

  useEffect(() => {
    if (isPartOfPilot && menuData.length) {
      // Validate that the subscription link is not already in the menu
      if (menuData.some((item) => item.title === "Subscription")) return;

      const myApplicationIndex = menuData.findIndex((item) => item.title === "My applications");
      const myApplication = menuData[myApplicationIndex];
      // Get domain from the current myApplication absolute url without remove the http or https part
      const domain = myApplication.absolute.match(/(https?:\/\/[^/]+)/)[1];
      const subscription = {
        ...myApplication,
        key: "fe9a4bb1-9a41-46fa-ab0f-dd14subs0123",
        title: "Subscription",
        absolute: `${domain}/subscription/`,
        relative: "/subscription/",
        options: {}
      }

      const newMenuData = [...menuData];
      newMenuData.splice(myApplicationIndex + 1, 0, subscription);

      setMenuData(newMenuData);
    }
  }, [isPartOfPilot, menuData]);

  useEffect(() => {
    // Disable links based on trial status and feature flags
    let old_links = menuData;
    let new_links = [];
    old_links.map((link) => {
      // Check if Usage analytics should be hidden based on feature flag
      if (flags['disable_trial_status'] && link.relative === '/myaccount/') {
        return;
      }

      if (link.options?.attributes?.class?.length) {
        if (isTrial && link.options.attributes.class.includes('user-only')) {
          return;
        }
        if (!isTrial && link.options.attributes.class.includes('trial-only')) {
          return
        }
        if (isInternal && link.options.attributes.class.includes("external-only")) {
          return;
        }
        if (!isInternal && link.options.attributes.class.includes("internal-only")) {
          return;
        }
      }
      new_links.push(link);
    });
    setMenuData(new_links);
  }, [isTrial, isLoaded, flags['disable_trial_status']]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (elementRef.current && !elementRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [elementRef]);

  const expand = (e) => {
    e.stopPropagation();
    if (isUser) {
      e.preventDefault();
      setOpen(!isOpen);
    }
  };

  const close = (e) => {
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <div className={`aps__profile ${isLoaded ? 'is-loaded' : ''} ${isUser ? 'is-user' : ''} ${isOpen ? 'is-open' : ''}`}
         ref={elementRef}>
      {isUser
        ? <Menu key={`menu`}
                className={`aps__profile-menu`}
                align={`right`}
                open={profileIcon}>
          <Dropdown key={`dropdown`}
                    classes={`aps__profile-dropdown`}
                    align={`right`}
                    open={profileIcon}>
            <Column key={`profile`}
                    links={menuData}>
              <div key={`user-name`}
                   className={`aps__user-name`}>
                {profileData?.first_name} {profileData?.last_name}
              </div>
              <div key={`user-email`}
                   className={`aps__user-email`}>
                {profileData?.email}
              </div>
            </Column>
          </Dropdown>
        </Menu>
        : <Link key={`user-login`}
                className={`aps__link`}
                link={buildResolveUrl(`/user/login`)}>
          {profileIcon}
          <span key={`link`} className={`aps__profile-login`}>Sign in</span>
        </Link>
      }
    </div>
  );
}
